import React, {
  createContext,
  useContext,
  useRef,
  useState,
} from 'react';
import { Tooltip } from 'antd';
import styled from 'styled-components';

/**
 * When false, truncated ScopeButton labels do not open their own tooltip.
 * Use around an outer Tooltip/Popover so only one popup shows on hover.
 */
export const ScopeButtonLabelTooltipContext = createContext(true);

export const SuppressScopeButtonLabelTooltip: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => (
  <ScopeButtonLabelTooltipContext.Provider value={false}>
    {children}
  </ScopeButtonLabelTooltipContext.Provider>
);

/** True when a tooltip/popover title should take precedence over label truncation. */
export const hasVisibleOverlayTitle = (title: unknown): boolean => {
  if (title == null || title === false || title === '') return false;
  if (typeof title === 'string' && title.trim() === '') return false;
  return true;
};

const Label = styled.span`
  display: inline-block;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
`;

const getLabelText = (children: React.ReactNode): string | undefined => {
  if (typeof children === 'string' || typeof children === 'number') {
    return String(children);
  }

  return undefined;
};

const normalizeTooltipText = (value: unknown): string | undefined => {
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value).trim();
  }
  return undefined;
};

type ButtonLabelChildProps = {
  className?: string;
  children?: React.ReactNode;
};

const shouldSkipWrapping = (child: React.ReactNode): boolean => {
  if (!React.isValidElement<ButtonLabelChildProps>(child)) {
    return false;
  }

  const className = child.props.className;

  if (typeof className === 'string') {
    if (
      className.includes('anticon') ||
      className.includes('ant-btn-icon') ||
      className.includes('show-on-hover') ||
      className.includes('scope-sso-qbo-hover') ||
      className.includes('testpic')
    ) {
      return true;
    }
  }

  return child.type === 'svg';
};

export const ScopeButtonLabel: React.FC<{
  children?: React.ReactNode;
  className?: string;
  /** Outer tooltip title; if it matches the label, skip the truncation tooltip. */
  outerTitle?: unknown;
}> = ({ children, className, outerTitle }) => {
  const [open, setOpen] = useState(false);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const allowTruncationTooltip = useContext(ScopeButtonLabelTooltipContext);
  const labelText = getLabelText(children);
  const outerText = normalizeTooltipText(outerTitle);
  const sameAsOuter =
    !!labelText && !!outerText && labelText.trim() === outerText;

  const clearHoverTimer = () => {
    if (hoverTimer.current != null) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  };

  const showIfTruncated = (element: HTMLElement) => {
    clearHoverTimer();
    const truncated = element.scrollWidth > element.clientWidth;
    if (!truncated || !labelText || !allowTruncationTooltip || sameAsOuter) {
      setOpen(false);
      return;
    }
    hoverTimer.current = setTimeout(() => setOpen(true), 350);
  };

  const hide = () => {
    clearHoverTimer();
    setOpen(false);
  };

  return (
    <Tooltip title={labelText} open={open} mouseEnterDelay={0.35}>
      <Label
        className={['scope-button-label', className].filter(Boolean).join(' ')}
        onPointerEnter={(event) => showIfTruncated(event.currentTarget)}
        onFocus={(event) => showIfTruncated(event.currentTarget)}
        onPointerLeave={hide}
        onBlur={hide}
      >
        {children}
      </Label>
    </Tooltip>
  );
};

export const wrapButtonLabelChildren = (
  children: React.ReactNode,
  outerTitle?: unknown
): React.ReactNode =>
  React.Children.map(children, (child) => {
    if (typeof child === 'string' || typeof child === 'number') {
      return (
        <ScopeButtonLabel key="scope-button-label" outerTitle={outerTitle}>
          {child}
        </ScopeButtonLabel>
      );
    }

    if (!React.isValidElement<ButtonLabelChildProps>(child) || shouldSkipWrapping(child)) {
      return child;
    }

    if (
      child.type === 'span' &&
      (typeof child.props.children === 'string' ||
        typeof child.props.children === 'number')
    ) {
      return (
        <ScopeButtonLabel
          key="scope-button-label"
          className={child.props.className}
          outerTitle={outerTitle}
        >
          {child.props.children}
        </ScopeButtonLabel>
      );
    }

    return child;
  });

export const withTruncatedButtonLabel = <P extends { children?: React.ReactNode }>(
  StyledButton: React.ComponentType<P>
) => {
  const ButtonWithTruncatedLabel = React.forwardRef<unknown, P>(
    ({ children, ...rest }, ref) => (
      <StyledButton ref={ref} {...(rest as unknown as P)}>
        {wrapButtonLabelChildren(children)}
      </StyledButton>
    )
  );

  ButtonWithTruncatedLabel.displayName = `WithTruncatedButtonLabel(${
    StyledButton.displayName || StyledButton.name || 'Button'
  })`;

  return ButtonWithTruncatedLabel;
};
